/* eslint-disable react/jsx-pascal-case */
import React, { useEffect, useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import styled, { css, keyframes } from 'styled-components';
import logo from '~/images/pp_logo.svg';
import {
  getOrganizationApplicationSettingOrNull,
  OrganizationApplicationSetting,
} from '../../../actions/organization';
import { setUserPrivateMoney } from '../../../actions/user-private-money';
import { useAppDispatch, useAppSelector } from '../../../hooks/useApp';
import { getFrontEnv } from '../../../utils';
import fuzzySearch from '../../../utils/fuzzySearch';
import FA from '../../atoms/FontAwesome';
import Input from '../../atoms/Input';

const LabelText = styled.span`
  padding: 5px;
  margin: 10px;
  text-align: center;
  color: white;
  background-color: red;
  font-weight: 600;
`;

const Logo = styled.img`
  height: 30px;
`;

const Wrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 999;
  background-color: white;
  width: 100%;
  display: flex;
  align-items: center;
  height: 48px;
`;

const LogoSection = styled.section`
  display: flex;
  align-items: center;
  margin-left: 32px;
`;

const ActionSection = styled.section`
  display: flex;
  margin-left: auto;
  padding: 0 16px;
  align-items: center;
  gap: 16px;

  .dropdown-item {
    a {
      color: black;
      &.logout {
        color: red;

        &:active {
          color: white;
        }
      }
    }

    &.active,
    &:active {
      background-color: #00a23f;
      color: white;
    }
  }
`;

const actionStyles = css`
  padding: 4px 12px;
  font-size: 0.9rem;
  border-radius: 999px;
`;

const PrivateMoneyWrapper = styled.div`
  ${actionStyles}
  background-color: transparent;
  color: #00a23f;
  border: #00a23f;
  cursor: pointer;
`;

const ProfileWrapper = styled.div`
  ${actionStyles}
  cursor: pointer;

  .dropdown-menu {
    min-width: 280px;
  }
`;

const Muted = styled.span`
  color: grey;
`;

const UserInfoWrapper = styled.div`
  padding: 4px 24px 12px;
  margin-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;

  .avatar {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    margin-right: 12px;
    background-color: #00a23f;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    padding: 4px;
  }

  .info {
    display: flex;
    flex-direction: column;

    .name {
      font-weight: 600;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .role {
      font-size: 0.8rem;
      color: grey;
    }
  }
`;

const HighlightAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
`;

const Highlighter = styled.div<{
  isHighlighted?: boolean;
}>`
  transition: all 0.2s ease-in-out;
  border: 1px solid transparent;
  border-radius: 999px;
  border-color: transparent;
  background: transparent;
  color: unset;
  padding: 4px 12px;
  ${({ isHighlighted }) =>
    isHighlighted
      ? css`
          border-color: #00a23f;
          background: #00a23f;
          color: white;
          animation: ${HighlightAnimation} 1s infinite;
        `
      : css``}
`;

const DropdownItemInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SearchInput = styled(Input)`
  margin: 8px 12px;
  min-width: 200px;
`;

const UserInfo = ({ name, role }: { name: string; role: string }) => (
  <UserInfoWrapper>
    <div className="avatar">
      <FA icon="user" />
    </div>
    <div className="info">
      <span className="name">{name}</span>
      <span className="role">{role}</span>
    </div>
  </UserInfoWrapper>
);

const OrgInfoWrapper = styled.div`
  padding: 4px 24px 12px;
  margin-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.85rem;
  text-align: left;

  .row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 2px 0;
    margin: 0;
  }

  .heading {
    color: grey;
    margin-top: 4px;
    margin-bottom: 6px;
    padding-bottom: 4px;
    border-bottom: 1px solid #e9ecef;
  }

  .label {
    color: grey;
  }

  .value {
    text-align: right;
    word-break: break-all;
  }
`;

type OrgInfoProps = {
  code?: string;
  name?: string;
  prefix?: string;
};

const OrgInfo = ({ code, name, prefix }: OrgInfoProps) => (
  <OrgInfoWrapper>
    <div className="row heading">
      <span>組織情報</span>
    </div>
    <div className="row">
      <span className="label">組織コード</span>
      <span className="value">{code || '-'}</span>
    </div>
    <div className="row">
      <span className="label">組織名</span>
      <span className="value">{name || '-'}</span>
    </div>
    {prefix && (
      <div className="row">
        <span className="label">プレフィックス</span>
        <span className="value">{prefix}</span>
      </div>
    )}
  </OrgInfoWrapper>
);

const Header = () => {
  const user = useAppSelector((state) => state.user);
  const { selectedMoney, availableMoneys, isHighlighted, allowedMoneys } = useAppSelector(
    (state) => state.userPrivateMoney
  );

  const dispatch = useAppDispatch();

  const [search, setSearch] = useState('');

  const env = getFrontEnv();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [moneyDropdownOpen, setMoneyDropdownOpen] = useState(false);

  const toggle = () => setDropdownOpen((prev) => !prev);

  const multipleMoney = availableMoneys.length > 1;

  const filteredMoneys = availableMoneys.filter((money) => fuzzySearch(search, money?.name ?? ''));

  // 発行体ログインかつ管理者ロールのときのみ加盟店申込み設定 (prefix) を取得する。
  // ORGANIZATION-STAFF は backend 側で read を許可していないので除外する。
  const isIssuerLogin =
    user.role === 'ORGANIZATION-MANAGER' && !!user.isIssuerOf?.('');
  const [applicationSetting, setApplicationSetting] =
    useState<OrganizationApplicationSetting | null>(null);

  useEffect(() => {
    if (!isIssuerLogin) {
      setApplicationSetting(null);
      return undefined;
    }
    let cancelled = false;
    getOrganizationApplicationSettingOrNull().then(
      (setting) => {
        if (!cancelled) setApplicationSetting(setting);
      },
      () => {
        if (!cancelled) setApplicationSetting(null);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [isIssuerLogin]);

  return (
    <Wrapper>
      <LogoSection>
        <a href="/">
          <Logo src={logo} alt="Pokepay Logo" />
          {env !== 'production' ? <LabelText>テスト環境 ({env})</LabelText> : null}
        </a>
      </LogoSection>
      <ActionSection>
        {multipleMoney && (
          <PrivateMoneyWrapper>
            <Dropdown
              isOpen={moneyDropdownOpen}
              toggle={() => setMoneyDropdownOpen((prev) => !prev)}
            >
              <Highlighter isHighlighted={isHighlighted}>
                <DropdownToggle caret className="text-truncate" tag="span">
                  {selectedMoney.name}
                </DropdownToggle>
              </Highlighter>
              <DropdownMenu>
                <SearchInput
                  syncValue={search}
                  placeholder="マネー名..."
                  onChange={(e) => setSearch(e.target.value)}
                />
                {filteredMoneys.map((money) => (
                  <DropdownItem
                    key={money.id}
                    tag="div"
                    active={selectedMoney.id === money.id}
                    onClick={() => dispatch(setUserPrivateMoney(money))}
                  >
                    <DropdownItemInner>
                      {money.name}
                      {allowedMoneys.some((m) => m.id === money.id) && (
                        <FA icon="check-circle" color="#00a23f" />
                      )}
                    </DropdownItemInner>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </PrivateMoneyWrapper>
        )}
        <ProfileWrapper>
          <Dropdown isOpen={dropdownOpen} toggle={toggle}>
            <DropdownToggle caret className="text-truncate" tag="span">
              {!multipleMoney && (
                <>
                  <b style={{ opacity: 0.8 }}>{selectedMoney.name}</b> |{' '}
                </>
              )}
              <Muted>{user.organization?.name || ''}</Muted> | {user.email}
            </DropdownToggle>
            <DropdownMenu
              right
              flip
              modifiers={{
                preventOverflow: {
                  boundariesElement: 'viewport',
                  padding: 16,
                },
              }}
            >
              <UserInfo name={user.name ?? ''} role={user.role ?? ''} />
              {user.organization && (
                <OrgInfo
                  code={user.organization.code}
                  name={user.organization.name}
                  prefix={applicationSetting?.member_organization_code_prefix}
                />
              )}
              <DropdownItem tag="div">
                <a href="/logout" className="logout">
                  ログアウト
                </a>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </ProfileWrapper>
      </ActionSection>
    </Wrapper>
  );
};

export default Header;
